// EN-1196 merge dialog.
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { v4 as uuid } from "uuid";
import HDDialog from "@/common/components/HDDialog";
import Spinner from "@/common/components/Spinner";
import { SegmentedControl } from "@/common/components/SegmentedControl";
import { showActionToast } from "@/common/components/ActionToast";
import { useAppDispatch, useAppSelector, useAppStore } from "@/common/hooks/useRedux";
import type { RootState } from "@/app/store";
import {
    FieldOverrides,
    fetchMergeStatus,
    MERGE_FAILED,
    MergeBlockReason,
    MergePreview,
    MergeResult,
    OVERRIDE_FIELDS,
    postMerge,
    postUnmerge,
} from "@/common/lib/api/visitMerges";
import { useInvalidateMergeState, useMergeCandidates, useMergePreview } from "@/state/hooks/useVisitMerges";
import { closeMergeDialog, markMergeDialogOpenedAt } from "@/state/redux/mergeVisitsUiSlice";
import { removeRecords } from "@/state/redux/patientTableRecordsSlice";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { fetchPatientData, getPatientData } from "@/features/patient-view/state/redux/activePatientDataSlice";
import { COPY, formatTime, formatVisitWhen, reasonText, unmergeReasonText } from "./copy";
import { usePimsName } from "./usePimsName";
import { mergedToastId, unmergedToastId } from "./mergedToastId";
import { VisitPickerList } from "./VisitPickerList";
import { KeptDetailsOverrides, suggestedOverrides, summarizeKept, useDetailNames } from "./KeptDetailsOverrides";
import { formatLocalDay, sameLocalDay, samePatientName } from "./visitCompare";
import { TOUCH_MIN_H } from "@/common/utils/touchTarget";
import { MergeManifest } from "./MergeManifest";
import { pollMergeStatus } from "./pollMergeStatus";
import { confirmedUnmerged, unmergeMayHaveWorked } from "./confirmUnmerge";

// "failed" is a definite failure (nothing moved); "unconfirmed" is an unknown outcome the status poll never resolved.
type Phase = "edit" | "submitting" | "slow" | "success" | "blocked" | "failed" | "unconfirmed";
const SLOW_AFTER_MS = 25_000;

/**
 * Overrides including Room refused for a kept visit the preview showed unlinked. The preview may be stale: the
 * visit has its own rooms, or was linked since. Either way Room can't be set; Back drops it and reloads the preview.
 */
const isRoomRefused = (r: MergeResult, sent: FieldOverrides, keptLinked: boolean) =>
    !r.ok && r.kind === "error" && r.code === "OVERRIDES_NOT_ALLOWED" && !keptLinked && !!sent.resource_id;

/** Refusals that do not change with time: Try again would be refused the same way, so only Close is offered. */
const PERMANENT_REFUSALS = new Set<MergeBlockReason>([
    "same_visit",
    "account_mismatch",
    "history_visit",
    "already_archived",
    "different_pims_patient",
    "document_conflict",
    "idempotency_key_reused",
]);

/** The copy for a merge failure that retrying will not fix, or null when Try again may work. */
const finalFailureOf = (r: MergeResult, roomRefused: boolean): string | null =>
    r.ok
        ? null
        : r.kind === "audio" && !r.retryable
          ? COPY.mergeNotAutomatic
          : roomRefused
            ? COPY.roomNotAllowed
            : r.kind === "error" && r.code === "OVERRIDES_NOT_ALLOWED"
              ? COPY.overridesNotAllowed
              : null;

/**
 * Keeping a visit takes the other visit's details where the kept one has none, so nothing is lost by default,
 * except for a linked kept visit (sync owns its details), Room onto a visit with its own rooms, and Time.
 */
const overridesFor = (preview: MergePreview, keptId: string, emptiedId: string): FieldOverrides => {
    const kept = preview.visits[keptId];
    const emptied = preview.visits[emptiedId];
    return kept && emptied ? suggestedOverrides(kept, emptied) : {};
};

const timeOf = (v?: { scheduledAt: string | null; createdAt: string }) =>
    v ? formatTime(v.scheduledAt, v.createdAt) : "";
const label = "text-[11px] font-bold uppercase tracking-wider text-gray-500";

/**
 * Neither no answer nor a plain 500 proves nothing happened (the server answers 500 when its own RPC
 * outcome is unknown): ask by idempotency key before claiming anything. Finding nothing still proves
 * nothing, so that stays "unknown".
 */
async function resolveUnknownOutcome(idempotencyKey: string): Promise<MergeResult> {
    const status = await pollMergeStatus({
        fetch: () => fetchMergeStatus(idempotencyKey),
        want: "merged",
        waitWhileMissing: true,
        retryOnError: true,
    });
    return status
        ? { ok: true, mergeId: status.mergeId, audioUnconfirmed: status.audioUnconfirmed }
        : { ok: false, kind: "unknown" };
}

/** How often a picked visit that left the candidates list is checked by id (matches the list's 10 s refetch). */
const OUT_OF_LIST_CHECK_MS = 10_000;
export function MergeVisitsDialog({ sourceId, initialTargetId }: { sourceId: string; initialTargetId: string | null }) {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const invalidate = useInvalidateMergeState();
    const { refreshPatientRecords } = useDataContextApi();
    const names = useDetailNames();
    const pimsName = usePimsName();
    const [otherId, setOtherId] = useState<string | null>(initialTargetId);
    const [keptId, setKeptId] = useState<string | null>(null);
    const [overrides, setOverrides] = useState<FieldOverrides>({});
    const [showOverrides, setShowOverrides] = useState(false);
    // The vet ticked that a visit for another patient name, or on another day, is the same appointment.
    const [confirmedSame, setConfirmedSame] = useState(false);
    const [picking, setPicking] = useState(!initialTargetId);
    const [phase, setPhase] = useState<Phase>("edit");
    // null while blocked: a reason this build does not know.
    const [blockedReason, setBlockedReason] = useState<MergeBlockReason | null>(null);
    // Set for a failure the server says retrying will not fix: the copy to show instead of try-again.
    const [finalFailure, setFinalFailure] = useState<string | null>(null);
    // The final failure was a refused Room override: offer Back to the form to merge without it.
    const [roomRefused, setRoomRefused] = useState(false);
    // The server rolled back because another change held the visits: say so, and offer Try again.
    const [busyFailure, setBusyFailure] = useState(false);
    const [mergeId, setMergeId] = useState<string | null>(null);
    const [audioUnconfirmed, setAudioUnconfirmed] = useState(0);
    const [undoing, setUndoing] = useState(false);
    const idempotency = useRef({ choice: "", key: "" });
    const inFlight = useRef(false);
    const blockedAt = useRef(0);
    const seenOther = useRef<string | null>(null);
    // The search the vet picked the other visit from. The unfiltered list holds only the 30 newest visits,
    // so a visit found by search is watched with that same search.
    const [pickedQuery, setPickedQuery] = useState("");
    // The picked visit once it has left the candidates list: from then on it is checked by id (the preview).
    const [outOfList, setOutOfList] = useState<string | null>(null);
    const mounted = useRef(true);
    const slowTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // Merges undone (or being undone) from here, so the dialog's Undo and the toast's Undo never both fire.
    const undone = useRef(new Set<string>());
    const mergedToast = useRef<string | number | undefined>(undefined);
    // The kept visit's id, set when a merge succeeds while the vet is on the visit it emptied (archived
    // now, but its page still records): however the dialog closes, land on the kept visit instead.
    const leaveEmptiedFor = useRef<string | null>(null);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(slowTimer.current);
        };
    }, []);
    // A refusal on screen stays until the vet acts on it, even if the other visit has since left the list.
    // The recording block is a live wait instead, so it gives way when there is nothing left to wait for.
    const live = phase === "edit" || (phase === "blocked" && blockedReason === "recording_active");
    const checkById = live && !!otherId && outOfList === otherId;
    const {
        data: preview,
        isError: previewFailed,
        refetch: refetchPreview,
    } = useMergePreview(sourceId, otherId, checkById ? OUT_OF_LIST_CHECK_MS : false);

    // The host remounts per page and Redux keeps the dialog open, so close it when the page changes.
    const openedAt = useAppSelector((s) => s.mergeVisitsUi.dialog?.openedAt);
    // The opened visit as the page already has it (patient view, or the visit list row), for the picker
    // heading before a second visit is picked and a preview exists.
    const openedRecord = useAppSelector((s) => {
        const active = getPatientData(s);
        if (active?.id === sourceId) return active;
        return (s as Partial<RootState>).patientTableRecords?.tableRecords.find((r) => r.id === sourceId);
    });
    useEffect(() => {
        if (openedAt === undefined) dispatch(markMergeDialogOpenedAt(pathname));
        else if (openedAt !== pathname) dispatch(closeMergeDialog());
    }, [openedAt, pathname, dispatch]);

    // Live eligibility of the chosen visit (10 s refetch). Drives the recording watch and reconciles the selection.
    const watching = phase === "edit" || phase === "blocked";
    const { data: candidates, dataUpdatedAt } = useMergeCandidates(watching ? sourceId : null, pickedQuery);
    const rows = candidates?.rows;
    // The opened visit's own state (e.g. it is being recorded on): blocks merge with any visit.
    const selfBlock = candidates?.selfBlock ?? null;
    const row = otherId ? rows?.find((r) => r.visit.id === otherId) : undefined;
    useEffect(() => {
        if (!rows || !otherId || !live) return;
        if (row) {
            seenOther.current = otherId;
            setOutOfList(null);
        } else if (seenOther.current === otherId && outOfList !== otherId) {
            // It was a candidate and no longer is. That can be a deleted visit, or only one pushed out of the
            // newest-visits list, so from now on check it by id (the preview polls while checkById).
            setOutOfList(otherId);
            void refetchPreview();
        }
    }, [rows, row, otherId, live, outOfList, refetchPreview]);
    useEffect(() => {
        // The preview 404s a deleted or archived visit: drop the selection then, and only then.
        if (!checkById || !otherId || !(previewFailed || (preview && !preview.visits[otherId]))) return;
        seenOther.current = null;
        setOutOfList(null);
        setOtherId(null);
        setKeptId(null);
        setOverrides({});
        setConfirmedSame(false);
        setPicking(true);
        setPhase("edit");
        setBlockedReason(null);
    }, [checkById, otherId, previewFailed, preview]);
    useEffect(() => {
        const recording = phase === "blocked" && blockedReason === "recording_active";
        if (recording && row?.eligible && !selfBlock && dataUpdatedAt > blockedAt.current) {
            setPhase("edit");
            setBlockedReason(null);
        }
    }, [phase, blockedReason, row, selfBlock, dataUpdatedAt]);
    // undefined: nothing blocks. null: blocked for a reason this build does not know.
    const unavailable: MergeBlockReason | null | undefined = selfBlock
        ? selfBlock.reason
        : row && !row.eligible
          ? row.reason
          : undefined;

    useEffect(() => {
        if (preview && otherId && preview.visits[otherId] && !keptId) {
            const id = preview.suggestion.targetId;
            setKeptId(id);
            setOverrides(overridesFor(preview, id, id === sourceId ? otherId : sourceId));
        }
    }, [preview, otherId, keptId, sourceId]);

    const emptiedId = keptId && otherId ? (keptId === sourceId ? otherId : sourceId) : null;
    const kept = keptId ? preview?.visits[keptId] : undefined;
    const emptied = emptiedId ? preview?.visits[emptiedId] : undefined;
    const openedVisit = preview?.visits[sourceId];
    const otherVisit = otherId ? preview?.visits[otherId] : undefined;
    // A mis-tap in Recent visits must not put one patient's recording on another's visit unnoticed.
    const differentPatient =
        !!openedVisit && !!otherVisit && !samePatientName(openedVisit.patientName, otherVisit.patientName);
    const differentDay = !!openedVisit && !!otherVisit && !sameLocalDay(openedVisit, otherVisit);
    const needsConfirm = differentPatient || differentDay;
    const close = () => {
        dispatch(closeMergeDialog());
        if (leaveEmptiedFor.current) navigate(`/appointments/${leaveEmptiedFor.current}`);
    };
    const running = phase === "submitting" || phase === "slow";

    // Read at call time: a toast's Undo can fire after the dialog closed and the vet opened another visit.
    const refreshIfOpen = (visitId: string) => {
        if (getPatientData(store.getState())?.id === visitId) dispatch(fetchPatientData(visitId));
    };

    const undo = async (id: string, keptVisitId: string, emptiedTime: string): Promise<boolean> => {
        if (undone.current.has(id)) return true;
        undone.current.add(id);
        let u = await postUnmerge(id);
        let mayHaveWorked = false;
        if (!u.ok && unmergeMayHaveWorked(u)) {
            mayHaveWorked = true;
            // It may still be running, or already undone (another tab, a lost reply).
            const status = await confirmedUnmerged(id, u);
            if (status) u = { ok: true, mergeId: id, audioUnconfirmed: status.audioUnconfirmed };
        }
        invalidate();
        if (u.ok) {
            // The emptied visit is back, so there is nothing to leave.
            leaveEmptiedFor.current = null;
            if (mergedToast.current !== undefined) toast.dismiss(mergedToast.current);
            void refreshPatientRecords();
            refreshIfOpen(keptVisitId);
            const toastId = unmergedToastId(id);
            if (u.audioUnconfirmed) toast.warning(COPY.unmergedAudioUnconfirmed(u.audioUnconfirmed), { toastId });
            else toast.success(COPY.unmergedToast(emptiedTime), { toastId });
            return true;
        }
        undone.current.delete(id);
        // It may have committed after all: the vet is told to check the visit list, so make it current, and the
        // kept visit's page too (a late unmerge took clips and documents off it).
        const unconfirmed = mayHaveWorked && u.kind !== "blocked";
        if (unconfirmed) {
            void refreshPatientRecords();
            refreshIfOpen(keptVisitId);
        }
        toast.error(
            u.kind === "blocked"
                ? unmergeReasonText(u.reason, pimsName)
                : u.kind === "busy"
                  ? COPY.busy
                  : u.kind === "audio" && !u.retryable
                    ? COPY.unmergeNotAutomatic
                    : unconfirmed
                      ? COPY.undoUnconfirmed
                      : COPY.undoFailed,
        );
        // A 409 (window passed, visit changed) is an expected answer, not an error. postUnmerge already
        // reported an audio failure with the server's reason.
        if (u.kind !== "blocked" && u.kind !== "audio" && u.kind !== "busy") {
            Sentry.captureException(new Error(`Undo merge failed: ${u.kind}`), {
                tags: { errorType: "visit_unmerge", errorCategory: u.kind === "unknown" ? "connectivity" : "code_bug" },
            });
        }
        return false;
    };

    const submit = async () => {
        if (!keptId || !emptiedId || inFlight.current) return;
        inFlight.current = true;
        // Same choice, same key: a retry after an unknown outcome replays instead of merging twice.
        const choice = `${emptiedId}>${keptId}|${OVERRIDE_FIELDS.filter((f) => overrides[f]).join(",")}`;
        if (idempotency.current.choice !== choice) idempotency.current = { choice, key: uuid() };
        const idempotencyKey = idempotency.current.key;
        setPhase("submitting");
        slowTimer.current = setTimeout(() => setPhase((p) => (p === "submitting" ? "slow" : p)), SLOW_AFTER_MS);
        // Sync owns a linked visit's details, and the server refuses overrides for one, and Room for a
        // visit with its own rooms.
        const { resource_id: _room, ...withoutRoom } = overrides;
        const sent: FieldOverrides = kept?.linked ? {} : kept?.hasOwnRooms ? withoutRoom : overrides;
        const keptLinked = !!kept?.linked;
        let r = await postMerge({ sourceId: emptiedId, targetId: keptId, overrides: sent, idempotencyKey });
        // Only no answer or a 5xx other than MERGE_FAILED may have committed: a 4xx is refused before the RPC,
        // and blocked, busy and audio failures are definite.
        const mayHaveCommitted =
            !r.ok &&
            (r.kind === "unknown" || (r.kind === "error" && (r.status ?? 500) >= 500 && r.code !== MERGE_FAILED));
        if (mayHaveCommitted) r = await resolveUnknownOutcome(idempotencyKey);
        clearTimeout(slowTimer.current);
        inFlight.current = false;
        if (!r.ok && r.kind === "unknown") {
            // It may have committed after all: the vet is told to check the visit list, so make it current, and the
            // kept visit's page too (a late merge put the other visit's clips and documents on it).
            invalidate();
            void refreshPatientRecords();
            refreshIfOpen(keptId);
        }
        if (r.ok) {
            const emptiedTime = timeOf(emptied);
            const id = r.mergeId;
            if (getPatientData(store.getState())?.id === emptiedId) leaveEmptiedFor.current = keptId;
            const unconfirmed = r.audioUnconfirmed ?? 0;
            invalidate();
            dispatch(removeRecords({ ids: [emptiedId] }));
            void refreshPatientRecords();
            refreshIfOpen(keptId);
            mergedToast.current = showActionToast({
                toastId: mergedToastId(id),
                message: unconfirmed > 0 ? COPY.mergedAudioUnconfirmed(unconfirmed) : COPY.mergedToast,
                actionLabel: COPY.undo,
                onAction: () => void undo(id, keptId, emptiedTime),
            });
            if (!mounted.current) {
                // Closed during "Still working": leave the emptied visit if the vet is still on its page.
                if (leaveEmptiedFor.current && window.location.pathname === `/appointments/${emptiedId}`) {
                    navigate(`/appointments/${keptId}`);
                }
                return;
            }
            setMergeId(id);
            setAudioUnconfirmed(unconfirmed);
            setPhase("success");
        } else if (!mounted.current) {
            // Closed during "Still working": the dialog is gone, so say how it ended in a toast.
            toast.error(
                r.kind === "blocked"
                    ? reasonText(r.reason, pimsName)
                    : r.kind === "unknown"
                      ? COPY.unconfirmedTitle
                      : r.kind === "busy"
                        ? COPY.busy
                        : (finalFailureOf(r, isRoomRefused(r, sent, keptLinked)) ?? COPY.failedTitle),
            );
        } else if (r.kind === "blocked") {
            blockedAt.current = Date.now();
            setBlockedReason(r.reason);
            setPhase("blocked");
        } else {
            const room = isRoomRefused(r, sent, keptLinked);
            setFinalFailure(finalFailureOf(r, room));
            setRoomRefused(room);
            setBusyFailure(r.kind === "busy");
            setPhase(r.kind === "unknown" ? "unconfirmed" : "failed");
        }
    };

    const exportDoc =
        preview && keptId
            ? (preview.manifests[keptId]?.documents[0]?.header ??
              preview.manifests[emptiedId ?? ""]?.documents[0]?.header)
            : undefined;
    const openKept = (focusExport: boolean) => {
        dispatch(closeMergeDialog());
        if (keptId) navigate(`/appointments/${keptId}${focusExport ? "?focus=export" : ""}`);
    };
    const pick = (id: string, query = "") => {
        setOtherId(id);
        setPickedQuery(query);
        setKeptId(null);
        setOverrides({});
        setShowOverrides(false);
        setConfirmedSame(false);
        setPicking(false);
    };

    const alert = (title: string, body?: string) => (
        <div role="alert" className="rounded-xl border border-blaze-100 bg-blaze-25 p-4 text-sm text-blaze-900">
            <p className="font-bold">{title}</p>
            {body && <p>{body}</p>}
        </div>
    );

    /** Each visit's content, doctor and role, so the two are told apart by more than their times. */
    const keepOptions = (p: MergePreview, a: string, b: string) => {
        const doctorOf = (id: string) => {
            const doctorId = p.visits[id].doctorId;
            const name = doctorId ? names.doctor(doctorId) : undefined;
            return name === COPY.noDoctor ? undefined : name;
        };
        const sameTitle = timeOf(p.visits[a]) === timeOf(p.visits[b]);
        const doctorsDiffer = doctorOf(a) !== doctorOf(b);
        return [a, b].map((id) => {
            const v = p.visits[id];
            const doctor = doctorOf(id);
            const suffix = !sameTitle
                ? ""
                : doctorsDiffer && doctor
                  ? ` · ${doctor}`
                  : id === sourceId
                    ? ` ${COPY.thisVisitSuffix}`
                    : "";
            return {
                value: id,
                title: `${timeOf(v)} visit${suffix}`,
                subtitle: [
                    COPY.keepContents(v.clipCount, v.docCount),
                    doctor,
                    v.linked ? COPY.linkedTo(pimsName) : id === sourceId ? COPY.thisVisit : undefined,
                ]
                    .filter(Boolean)
                    .join(" · "),
                badge: id === p.suggestion.targetId ? COPY.suggested : undefined,
            };
        });
    };

    const renderEdit = () => {
        const opened = preview?.visits[sourceId];
        const openedName = opened ? opened.patientName : openedRecord?.patient_name;
        const openedTime = opened
            ? timeOf(opened)
            : openedRecord?.created_at
              ? formatTime(openedRecord.scheduled_at ?? null, openedRecord.created_at)
              : "";
        const other = otherId ? preview?.visits[otherId] : undefined;
        // A failed refetch keeps the last preview; only a preview that never loaded shows the error.
        const previewDown = !preview && previewFailed;
        return (
            <div className="flex flex-col divide-y divide-gray-100">
                <section className="flex flex-col gap-2 pb-4">
                    {/* About the opened visit itself, so it sits above the other visit's row, not under it. */}
                    {selfBlock && (
                        <p className="text-sm text-blaze-900">
                            {COPY.thisVisitBlocked(reasonText(selfBlock.reason, pimsName))}
                        </p>
                    )}
                    {picking || !otherId ? (
                        <VisitPickerList
                            visitId={sourceId}
                            patientName={openedName ?? ""}
                            visitTime={openedTime}
                            selectedId={otherId}
                            pimsName={pimsName}
                            onSelect={pick}
                        />
                    ) : (
                        <>
                            <div className={`flex items-center justify-between ${label}`}>
                                {COPY.sectionWith}
                                <button
                                    type="button"
                                    className={`text-sm font-semibold normal-case tracking-normal text-max-900 ${TOUCH_MIN_H}`}
                                    onClick={() => setPicking(true)}
                                >
                                    {COPY.change}
                                </button>
                            </div>
                            {other && (
                                <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
                                    <span className="flex-1 text-sm font-bold">
                                        {other.patientName} · {formatVisitWhen(other.scheduledAt, other.createdAt)}
                                    </span>
                                    {other.linked && (
                                        <span className="rounded-md bg-max-50 px-2 py-px text-xs font-semibold text-max-950">
                                            {pimsName}
                                        </span>
                                    )}
                                </div>
                            )}
                            {row && !row.eligible && (
                                <p className="text-sm text-blaze-900">{reasonText(row.reason, pimsName)}</p>
                            )}
                            {other && opened && needsConfirm && (
                                <div
                                    role="alert"
                                    className="flex flex-col gap-2 rounded-lg border border-tropicana-300 bg-tropicana-25 px-3 py-2 text-sm text-tropicana-950"
                                >
                                    {differentPatient && (
                                        <p>
                                            {COPY.differentPatient(other.patientName ?? "", opened.patientName ?? "")}
                                        </p>
                                    )}
                                    {differentDay && <p>{COPY.differentDay(formatLocalDay(other))}</p>}
                                    <label className={`flex items-center gap-2 font-semibold ${TOUCH_MIN_H}`}>
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-max-900 focus:ring-max-700"
                                            checked={confirmedSame}
                                            onChange={(e) => setConfirmedSame(e.target.checked)}
                                        />
                                        {COPY.confirmSameAppointment}
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                    {picking && !otherId && <p className="pt-2 text-sm text-gray-500">{COPY.pickFirst}</p>}
                </section>
                {!picking && otherId && previewDown && (
                    <div className="flex flex-col items-start gap-3 py-4">
                        {alert(COPY.previewFailedTitle, COPY.previewFailedBody)}
                        <button
                            type="button"
                            className="rounded-md px-2.5 py-1 text-sm font-semibold ring-1 ring-inset ring-gray-300"
                            onClick={() => void refetchPreview()}
                        >
                            {COPY.retry}
                        </button>
                    </div>
                )}
                {!picking && otherId && !previewDown && !(preview && kept && emptied && emptiedId && keptId) && (
                    <div className="flex justify-center py-6">
                        <Spinner />
                    </div>
                )}
                {!picking && preview && kept && emptied && emptiedId && keptId && otherId && (
                    <>
                        <section className="flex flex-col gap-3 py-4">
                            <p className={label}>{COPY.sectionKeep}</p>
                            <SegmentedControl
                                label="Visit to keep"
                                value={keptId}
                                onChange={(v) => {
                                    setKeptId(v);
                                    setOverrides(overridesFor(preview, v, v === sourceId ? otherId : sourceId));
                                }}
                                options={keepOptions(preview, sourceId, otherId)}
                            />
                            {keptId === preview.suggestion.targetId && (
                                <p className="text-sm text-max-950">
                                    {preview.suggestion.reason === "linked"
                                        ? COPY.reasonLinked(pimsName)
                                        : preview.suggestion.reason === "content"
                                          ? COPY.reasonContent
                                          : opened?.linked && other?.linked
                                            ? COPY.reasonCurrentBothLinked(pimsName)
                                            : COPY.reasonCurrent}
                                </p>
                            )}
                            {kept.linked ? (
                                <div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                    <span>
                                        <span className="text-gray-500">{COPY.keptWillShow}</span>{" "}
                                        <span className="font-semibold">{summarizeKept(kept, emptied, {}, names)}</span>
                                    </span>
                                    <span className="text-gray-500">{COPY.linkedDetails(pimsName)}</span>
                                </div>
                            ) : showOverrides ? (
                                <KeptDetailsOverrides
                                    kept={kept}
                                    emptied={emptied}
                                    overrides={overrides}
                                    onChange={setOverrides}
                                    onDone={() => setShowOverrides(false)}
                                />
                            ) : (
                                <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                    <span className="text-gray-500">{COPY.keptWillShow}</span>
                                    <span className="font-semibold">
                                        {summarizeKept(kept, emptied, overrides, names)}
                                    </span>
                                    <button
                                        type="button"
                                        className="ml-auto rounded-md px-2.5 py-1 text-sm font-semibold ring-1 ring-inset ring-gray-300"
                                        onClick={() => setShowOverrides(true)}
                                    >
                                        {COPY.changeDetails}
                                    </button>
                                </div>
                            )}
                        </section>
                        <section className="flex flex-col gap-2 pt-4">
                            <p className={label}>{COPY.sectionMoves(timeOf(kept))}</p>
                            <MergeManifest
                                manifest={preview.manifests[emptiedId]}
                                sameTypeDocuments={preview.sameTypeDocuments}
                                resultLinked={!!preview.resultLinked[keptId]}
                            />
                        </section>
                    </>
                )}
            </div>
        );
    };

    const renderBody = () => {
        if (running) {
            return (
                <div className="flex flex-col gap-4">
                    <div
                        role="progressbar"
                        aria-label="Merge progress"
                        className="h-1.5 overflow-hidden rounded bg-max-50"
                    >
                        <div className="h-full w-1/3 animate-pulse rounded bg-max-900" />
                    </div>
                    <p className="text-sm text-gray-600" aria-live="polite">
                        {phase === "slow" ? COPY.stillWorking : COPY.mergingFooter}
                    </p>
                </div>
            );
        }
        if (phase === "success") {
            return (
                <div className="flex flex-col gap-4">
                    {kept && (
                        <p className="text-sm text-gray-600">
                            {COPY.successBody(
                                kept.patientName ?? "",
                                // The kept visit took the emptied visit's time when the vet chose it.
                                timeOf(!kept.linked && overrides.scheduled_at && emptied ? emptied : kept),
                            )}
                        </p>
                    )}
                    {audioUnconfirmed > 0 && (
                        <p
                            role="status"
                            className="rounded-lg border border-tropicana-300 bg-tropicana-25 px-3 py-2 text-sm text-tropicana-950"
                        >
                            {COPY.mergedAudioUnconfirmed(audioUnconfirmed)}
                        </p>
                    )}
                    {keptId && preview?.resultLinked[keptId] && exportDoc && (
                        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-max-200 bg-max-25 px-4 py-3">
                            <span className="flex-1 text-sm font-bold text-max-950">
                                {COPY.readyToExport(exportDoc, pimsName)}
                            </span>
                            <button
                                type="button"
                                className="rounded-md bg-max-900 px-4 py-2 text-sm font-semibold text-white"
                                onClick={() => openKept(true)}
                            >
                                {COPY.exportDoc}
                            </button>
                        </div>
                    )}
                </div>
            );
        }
        if (phase === "blocked") {
            if (blockedReason !== "recording_active") return alert(reasonText(blockedReason, pimsName));
            // The candidates say which visit holds the lease (the other one, or the opened one); otherwise name no visit.
            const leased =
                row?.reason === "recording_active" && otherId
                    ? preview?.visits[otherId]
                    : selfBlock?.reason === "recording_active"
                      ? preview?.visits[sourceId]
                      : undefined;
            return (
                <div className="flex flex-col gap-3">
                    {alert(
                        leased ? COPY.blockedRecordingTitle(timeOf(leased)) : COPY.reason.recording_active,
                        COPY.blockedRecordingBody,
                    )}
                    <p className="text-sm text-max-950">{COPY.liveCheck}</p>
                </div>
            );
        }
        if (phase === "failed") {
            return alert(COPY.failedTitle, finalFailure ?? (busyFailure ? COPY.busy : COPY.failedBody));
        }
        if (phase === "unconfirmed") return alert(COPY.unconfirmedTitle, COPY.unconfirmedBody);
        return renderEdit();
    };

    // Named as its Keep option is, so two visits at the same minute still read differently here.
    const keptTitle =
        preview && otherId && kept
            ? keepOptions(preview, sourceId, otherId).find((o) => o.value === keptId)?.title
            : undefined;

    const footer = (() => {
        const btn = "rounded-md px-3.5 py-2 text-sm font-semibold";
        const secondary = `${btn} bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50`;
        const primary = `${btn} bg-max-900 text-white hover:bg-max-950 disabled:bg-gray-100 disabled:text-gray-500`;
        if (phase === "submitting") return null;
        if (phase === "slow") {
            return (
                <button type="button" className={secondary} onClick={close}>
                    {COPY.close}
                </button>
            );
        }
        if (phase === "success") {
            return (
                <div className="flex w-full justify-between">
                    <button
                        type="button"
                        className={`${btn} text-max-900 disabled:text-gray-400`}
                        disabled={undoing || !mergeId || !keptId}
                        onClick={async () => {
                            if (!mergeId || !keptId) return;
                            setUndoing(true);
                            const ok = await undo(mergeId, keptId, timeOf(emptied));
                            setUndoing(false);
                            if (ok) close();
                        }}
                    >
                        {COPY.undoMerge}
                    </button>
                    <button type="button" className={secondary} onClick={() => openKept(false)}>
                        {COPY.openVisit}
                    </button>
                </div>
            );
        }
        if (phase === "blocked" || phase === "failed" || phase === "unconfirmed") {
            return (
                <div className="flex justify-end gap-3">
                    <button type="button" className={secondary} onClick={close}>
                        {COPY.close}
                    </button>
                    {phase === "failed" && roomRefused && (
                        <button
                            type="button"
                            className={primary}
                            onClick={() => {
                                const { resource_id: _room, ...rest } = overrides;
                                setOverrides(rest);
                                void refetchPreview();
                                setFinalFailure(null);
                                setRoomRefused(false);
                                setPhase("edit");
                            }}
                        >
                            {COPY.back}
                        </button>
                    )}
                    {!(phase === "failed" && finalFailure) &&
                        // Sending the same merge again can only be refused the same way.
                        !(phase === "blocked" && blockedReason !== null && PERMANENT_REFUSALS.has(blockedReason)) && (
                            <button type="button" className={primary} onClick={submit}>
                                {COPY.tryAgain}
                            </button>
                        )}
                </div>
            );
        }
        return (
            <div className="flex w-full flex-wrap items-center gap-3">
                <span className="flex-1 text-xs text-gray-500">{emptied ? COPY.footerNote(timeOf(emptied)) : ""}</span>
                <button type="button" className={secondary} onClick={close}>
                    {COPY.cancel}
                </button>
                <button
                    type="button"
                    className={primary}
                    disabled={
                        !kept || !emptied || picking || unavailable !== undefined || (needsConfirm && !confirmedSame)
                    }
                    onClick={submit}
                >
                    {keptTitle ? COPY.mergeInto(keptTitle) : COPY.mergeVisits}
                </button>
            </div>
        );
    })();

    return (
        <HDDialog
            isOpen
            handleClose={phase === "submitting" ? () => undefined : close}
            header={phase === "success" ? COPY.successTitle : running ? COPY.merging : COPY.title}
            subHeader={phase === "edit" ? COPY.subtitle : undefined}
            footer={footer}
            hideCloseButton={phase === "submitting"}
            closeOnClickOutside={!running}
            // Full-screen sheet below md-custom (926 px), a 768 px-wide modal above.
            widthClassName="w-full md-custom:w-[80%] md-custom:max-w-3xl"
            className="max-md-custom:min-h-screen max-md-custom:rounded-none"
        >
            {renderBody()}
        </HDDialog>
    );
}
